import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { faSkype } from '@fortawesome/free-brands-svg-icons/faSkype';
import { faTelegramPlane } from '@fortawesome/free-brands-svg-icons/faTelegramPlane';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons/faWhatsapp';
import { faHeadset } from '@fortawesome/pro-light-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faAt } from '@fortawesome/pro-regular-svg-icons/faAt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useOnclickOutside from 'react-cool-onclickoutside';
import Button from 'epn-ui/umd/dist/components/Button';

import BaseSemanticLink from '@base/BaseSemanticLink';
import routes from '@config/routes.json';
import useI18n from '@i18n';
import { IGetManagerDataFormatted } from '@api/types';

import IconButton from '../IconButton';
import IconButtonDropdown from '../IconButtonDropdown';

interface IManagerMenuProps {
    manager: IGetManagerDataFormatted | null;
    unreadCount: number;
}

const ManagerMenu: React.FC<IManagerMenuProps> = ({ manager, unreadCount }) => {
    const { t, locale } = useI18n('Base');
    const router = useRouter();
    const [active, setActive] = useState(false);
    const managerDropdown = useOnclickOutside(() => setActive(false));

    const contactsNotEmpty = useMemo(() => {
        return (
            manager?.contacts?.whatsapp ||
            manager?.contacts?.email ||
            manager?.contacts?.telegram ||
            manager?.contacts?.skype
        );
    }, [manager]);

    const goToSupport = () => {
        router.push(`/${locale}${routes.support.open}`);
    };

    return (
        <div ref={managerDropdown} className="flex h-full md:relative">
            <IconButton
                onClick={() => {
                    if (!manager?.name) {
                        goToSupport();
                        return;
                    }

                    setActive(!active);
                }}
                active={active}
                icon={active ? faTimes : faHeadset}
                unreadFlag={!!unreadCount}
                id="ManagerButton"
                aria-label="toggle manager"
            />

            {active && manager?.name && (
                <IconButtonDropdown className="!h-auto">
                    <div className="p-4 pb-[12px]">
                        <h3 className="p-0 mb-0 font-medium text-[16px] text-blueGray-800 whitespace-nowrap">
                            {t('Personal manager')}
                        </h3>
                    </div>
                    <div className="px-4 pb-4">
                        <p className="p-0 font-medium pb-[12px] text-[14px] text-blueGray-600 leading-[21px]">
                            {manager.name}
                        </p>
                        {contactsNotEmpty && (
                            <div>
                                <span className="text-blueGray-500 text-[12px] pb-[4px] leading-[18px]">
                                    {t('Contacts')}:
                                </span>
                                {!!manager.contacts.email && (
                                    <p className="flex flex-row items-center text-[14px] pb-[2px] leading-[21px] text-blueGray-600">
                                        <FontAwesomeIcon
                                            icon={faAt}
                                            className="text-[18px] ml-[4px] mr-[6px]"
                                        />
                                        <span>{manager.contacts.email}</span>
                                    </p>
                                )}
                                {!!manager.contacts.telegram && (
                                    <p className="flex flex-row items-center text-[14px] pb-[2px] leading-[21px] text-blueGray-600">
                                        <FontAwesomeIcon
                                            icon={faTelegramPlane}
                                            className="text-[18px] ml-[4px] mr-[6px]"
                                        />
                                        <span>{manager.contacts.telegram}</span>
                                    </p>
                                )}
                                {!!manager.contacts.whatsapp && (
                                    <p className="flex flex-row items-center text-[14px] pb-[2px] leading-[21px] text-blueGray-600">
                                        <FontAwesomeIcon
                                            icon={faWhatsapp}
                                            className="text-[18px] ml-[4px] mr-[6px]"
                                        />
                                        <span>{manager.contacts.whatsapp}</span>
                                    </p>
                                )}
                                {!!manager.contacts.skype && (
                                    <p className="flex flex-row items-center text-[14px] pb-[2px] leading-[21px] text-blueGray-600">
                                        <FontAwesomeIcon
                                            icon={faSkype}
                                            className="text-[18px] ml-[4px] mr-[6px]"
                                        />
                                        <span>{manager.contacts.skype}</span>
                                    </p>
                                )}
                            </div>
                        )}
                        <Button type="primary" className="w-full mt-[12px]">
                            <BaseSemanticLink to={routes.support.open}>
                                {t('Support')} {!!unreadCount && `(${unreadCount})`}
                            </BaseSemanticLink>
                        </Button>
                    </div>
                </IconButtonDropdown>
            )}
        </div>
    );
};

export default ManagerMenu;
